import Grid from "@mui/material/Grid";
import { format, parse } from "date-fns";
import ReportContainer from "../../components/ReportContainer/ReportContainer";
import { Services, useServices } from "../../services/Services";
import { ReportTypeEnum } from "../../type/enum";
import css from "./Report.module.css";
import { useParams, useNavigate } from "react-router-dom";
import { JSX, ReactNode, RefObject, useRef, useState } from "react";
import { Osdk } from "@osdk/client";
import { Analysis, User } from "@unruly-osdk/sdk";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { cleanHtmlForCopy } from "../../helper/helper";

interface ReportProps {
  reportType: ReportTypeEnum;
  openLinksInNewTab?: boolean;
  children?: ReactNode;
}

type ContainerElement = (props: SharedContainerProps) => JSX.Element;

export const Report = ({
  reportType,
  openLinksInNewTab = false,
}: ReportProps) => {
  const Container: ContainerElement = (() => {
    switch (reportType) {
      case ReportTypeEnum.Daily:
      case ReportTypeEnum.NewDaily:
        return DailyReportContainer;
      case ReportTypeEnum.Weekly:
        return WeeklyReportContainer;
      default:
        const impossible: never = reportType;
        throw new Error("Impossible: unknown report type: " + impossible);
    }
  })();
  const services = useServices();
  const { data: user } = services.User.useGetCurrent();
  const navigate = useNavigate();
  const reportContentRef = useRef<HTMLDivElement>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const copyReportToClipboard = async () => {
    try {
      if (!reportContentRef.current) {
        console.error("Report content element not found");
        return;
      }

      // Remove buttons and SVG icons from the content using DOM mutation
      const cleanedHtml = cleanHtmlForCopy(reportContentRef.current);
      // Create a ClipboardItem with both HTML and plain text formats
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([cleanedHtml], { type: "text/html" }),
        "text/plain": new Blob([reportContentRef.current.textContent || ""], {
          type: "text/plain",
        }),
      });

      // Write both formats to clipboard
      await navigator.clipboard.write([clipboardItem]);

      // Show success message briefly
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy report:", err);
    }
  };

  const handleOpenAnalysis = (
    analysis: Osdk.Instance<Analysis>,
    _eventId: string,
  ) => {
    // Navigate to the appropriate analysis route based on type
    const route =
      analysis.type === "event deep dive"
        ? `/event-analysis/${analysis.id}`
        : analysis.type === "topic deep dive"
          ? `/topic-analysis/${analysis.id}`
          : "/"; // fallback to home
    navigate(route);
  };

  return (
    <ReportWrapper
      reportType={reportType}
      openLinksInNewTab={openLinksInNewTab}
    >
      <Container
        services={services}
        user={user}
        reportContentRef={reportContentRef}
        copyReportToClipboard={copyReportToClipboard}
        copySuccess={copySuccess}
        handleOpenAnalysis={handleOpenAnalysis}
        reportType={reportType}
        openLinksInNewTab={openLinksInNewTab}
      />
    </ReportWrapper>
  );
};

interface SharedContainerProps {
  services: Services;
  user: Osdk.Instance<User> | null | undefined;
  reportContentRef: RefObject<HTMLDivElement | null>;
  copyReportToClipboard: () => Promise<void>;
  copySuccess: boolean;
  handleOpenAnalysis: (
    analysis: Osdk.Instance<Analysis>,
    _eventId: string,
  ) => void;
  reportType: ReportTypeEnum;
  openLinksInNewTab: boolean;
}

const DailyReportContainer: ContainerElement = ({
  services,
  user,
  reportContentRef,
  copyReportToClipboard,
  copySuccess,
  handleOpenAnalysis,
  reportType,
  openLinksInNewTab,
}) => {
  const params = useParams<{ date: string }>();
  const selectedDate: Date | undefined = params.date
    ? parse(params.date, "yyyy-MM-dd", new Date())
    : undefined;

  const { data: report, isFetched } = services.Report.useGetDailyReport(
    selectedDate,
    user?.id,
  );

  const reportDate = report?.reportDateRangeEnd
    ? parse(report?.reportDateRangeEnd, "yyyy-MM-dd", new Date())
    : undefined;

  const title = "Personalized Daily Brief";
  const subTitle = reportDate
    ? format(reportDate, "EEE, MMM dd, yyyy")
    : isFetched
      ? "No report found for date"
      : "";

  useReportDocumentTitle("Daily Report", !isFetched, title, subTitle);

  return (
    <ReportContainer
      ref={reportContentRef}
      userId={user?.id}
      title={title}
      subTitle={subTitle}
      isLoading={!isFetched}
      report={report}
      reportType={reportType}
      handleCopyText={copyReportToClipboard}
      openLinksInNewTab={openLinksInNewTab}
      copySuccess={copySuccess}
      onOpenAnalysis={handleOpenAnalysis}
    />
  );
};

const WeeklyReportContainer: ContainerElement = ({
  services,
  user,
  reportContentRef,
  copyReportToClipboard,
  copySuccess,
  handleOpenAnalysis,
  reportType,
  openLinksInNewTab,
}) => {
  const params = useParams<{ date: string }>();
  const selectedDate: Date | undefined = params.date
    ? parse(params.date, "yyyy-MM-dd", new Date())
    : undefined;

  const { data: report, isFetched } = services.Report.useGetWeeklyReport(
    selectedDate,
    user?.id,
  );

  const title = "Personalized Weekly Brief";

  const startDate = report?.reportdateRangeStart
    ? parse(report.reportdateRangeStart, "yyyy-MM-dd", new Date())
    : undefined;
  const endDate = report?.reportDateRangeEnd
    ? parse(report.reportDateRangeEnd, "yyyy-MM-dd", new Date())
    : undefined;

  const subTitle =
    startDate && endDate
      ? `${format(startDate, "EEE, MMM dd, yyyy")} — ${format(endDate, "EEE, MMM dd, yyyy")}`
      : "No date range available";

  useReportDocumentTitle("Weekly Report", !isFetched, title, subTitle);

  return (
    <ReportContainer
      ref={reportContentRef}
      userId={user?.id}
      title={title}
      subTitle={subTitle}
      isLoading={!isFetched}
      report={report}
      reportType={reportType}
      handleCopyText={copyReportToClipboard}
      openLinksInNewTab={openLinksInNewTab}
      copySuccess={copySuccess}
      onOpenAnalysis={handleOpenAnalysis}
    />
  );
};

const ReportWrapper = ({ children }: ReportProps) => {
  return (
    <div className={css["report-container"]}>
      <div className={css["containers"]}>
        <Grid container spacing={2}>
          <Grid size="grow">{children}</Grid>
        </Grid>
      </div>
    </div>
  );
};

function useReportDocumentTitle(
  loadingType: string,
  isLoading: boolean,
  title: string,
  subTitle: string,
): void {
  const pageTitle = isLoading ? `Loading ${loadingType}...` : title;
  const pageSubtitle = isLoading ? "" : subTitle;
  useDocumentTitle(pageTitle, pageSubtitle);
}
